import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    name: obj?.name,
    slug: obj?.slug,
    description: obj?.description,
    country: obj?.country,
    logoPath: obj?.logo_path,
    logoUrl: obj?.logo_url,
    websiteUrl: obj?.website_url,
    parentCompany: obj?.parent_company,
    websiteLink: obj?.website_link,
    categoryId: obj?.category_id,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at
  };
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  return {
    name: obj?.name,
    slug: obj?.slug || null,
    description: obj?.description,
    country: obj?.country,
    logo_path: obj?.logoPath,
    logo_url: obj?.logoUrl,
    website_url: obj?.websiteUrl,
    parent_company: obj?.parentCompany,
    website_link: obj?.websiteLink,
    category_id: obj?.categoryId
  };
};

export const manufacturerService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('manufacturers')?.select('*')?.order('name');

      if (error) {
        console.error('Error fetching manufacturers:', error);
        throw error;
      }

      const result = data?.map(toCamelCase) || [];

      return result;
    } catch (err) {
      console.error('❌ manufacturerService.getAll caught error:', error);
      throw error;
    }
  },

  async getById(id) {
    const { data, error } = await supabase?.from('manufacturers')?.select('*')?.eq('id', id)?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  async getBySlug(slug) {
    const { data, error } = await supabase?.from('manufacturers')?.select('*')?.eq('slug', slug)?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  async create(manufacturer) {
    const { data, error } = await supabase?.from('manufacturers')?.insert(toSnakeCase(manufacturer))?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async update(id, manufacturer) {
    const { data, error } = await supabase?.from('manufacturers')?.update(toSnakeCase(manufacturer))?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id) {
    try {
      console.log('📡 manufacturerService.delete called for ID:', id);
      
      // CRITICAL FIX: Fetch manufacturer first to get logo_path before deletion
      const { data: manufacturer, error: fetchError } = await supabase
        ?.from('manufacturers')
        ?.select('logo_path')
        ?.eq('id', id)
        ?.single();

      if (fetchError) {
        console.error('❌ Error fetching manufacturer for deletion:', fetchError);
        throw fetchError;
      }

      console.log('📸 Manufacturer logo_path to delete:', manufacturer?.logo_path);

      // Delete logo file from storage if it exists
      if (manufacturer?.logo_path) {
        console.log('🗑️ Deleting logo file from storage:', manufacturer?.logo_path);
        const { error: storageError } = await supabase?.storage
          ?.from('company-logos')
          ?.remove([manufacturer?.logo_path]);

        if (storageError) {
          console.error('⚠️ Error deleting logo from storage:', storageError);
          // Continue with manufacturer deletion even if logo deletion fails
        } else {
          console.log('✅ Logo file deleted successfully');
        }
      }

      // Delete manufacturer record from database
      const { error: deleteError } = await supabase
        ?.from('manufacturers')
        ?.delete()
        ?.eq('id', id);
      
      if (deleteError) {
        console.error('❌ Error deleting manufacturer:', deleteError);
        throw deleteError;
      }

      console.log('✅ Manufacturer deleted successfully');
    } catch (error) {
      console.error('❌ manufacturerService.delete caught error:', error);
      throw error;
    }
  },

  async uploadLogo(manufacturerId, file) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `manufacturer-${manufacturerId}-${Date.now()}.${fileExt}`;
      const filePath = `manufacturers/${fileName}`;

      // Upload file to storage
      const { error: uploadError } = await supabase?.storage
        ?.from('company-logos')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase?.storage
        ?.from('company-logos')
        ?.getPublicUrl(filePath);

      const logoUrl = data?.publicUrl;

      // Update manufacturer with logo_url (full URL) and logo_path (relative path)
      const { error: updateError } = await supabase
        ?.from('manufacturers')
        ?.update({ 
          logo_url: logoUrl, // Store full public URL
          logo_path: filePath, // Store relative path for reference
          updated_at: new Date()?.toISOString() // Force timestamp update
        })
        ?.eq('id', manufacturerId);

      if (updateError) throw updateError;

      return logoUrl;
    } catch (error) {
      console.error('Logo upload error:', error);
      throw new Error(error?.message || 'Failed to upload logo');
    }
  },

  async deleteLogo(manufacturerId, logoPath) {
    try {
      // Delete from storage if logoPath exists
      if (logoPath) {
        const { error: deleteError } = await supabase?.storage
          ?.from('company-logos')
          ?.remove([logoPath]);

        if (deleteError) throw deleteError;
      }

      // Update manufacturer to remove both logo_path and logo_url
      const { error: updateError } = await supabase
        ?.from('manufacturers')
        ?.update({ 
          logo_path: null,
          logo_url: null
        })
        ?.eq('id', manufacturerId);

      if (updateError) throw updateError;
    } catch (error) {
      console.error('Logo deletion error:', error);
      throw new Error(error?.message || 'Failed to delete logo');
    }
  },

  getLogoUrl(logoPath) {
    if (!logoPath) return null;
    
    const { data } = supabase?.storage
      ?.from('company-logos')
      ?.getPublicUrl(logoPath);
    
    // Add cache-busting parameter to force fresh image load
    const timestamp = new Date()?.getTime();
    return data?.publicUrl ? `${data?.publicUrl}?t=${timestamp}` : null;
  }
};